import React, { useState } from 'react';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Backdrop,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Editor } from '@tinymce/tinymce-react';
import { useNavigate } from 'react-router-dom';
import HttpService from '../../../hooks/Https-services';
import PAGE_ROUTES, {
  API_ROUTES,
  BROADCAST_NOTIFICATION_TYPE,
} from '../../../utils/constant';

function AddBroadcastNotification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [editorContent, setEditorContent] = useState(''); // State for TinyMCE content
  const [teams, setTeams] = useState<any>([]);
  const fetchTeamsList = React.useCallback(async () => {
    try {
      const response = await HttpService.get(API_ROUTES.fatchAllTeam);
      const data: any = response.data.data.filter(
        (team: { userTeamName: string }) => team?.userTeamName !== 'all_teams'
      );

      const ROLE_IDS: any = {
        ADMIN: { id: 1, roleId: 1, teamId: null },
        MODERATOR: { id: 2, roleId: 2, teamId: null },
        SUPER_ADMIN: { id: 4, roleId: 4, teamId: null },
      };

      data.forEach((team: any) => {
        const key = `TAGGER_${team.userTeamKey.replace(/\s+/g, '_').toUpperCase()}`;
        ROLE_IDS[key] = { id: team.id, roleId: 3, teamId: team.id };
      });

      setTeams(ROLE_IDS);
    } catch (error: any) {
      console.error('Error fetching games list:', error);
    }
  }, []);

  React.useEffect(() => {
    fetchTeamsList();
  }, [fetchTeamsList]);
  const validationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    roleId: Yup.array()
      .min(1, 'At least one role is required')
      .required('Role is required'),
    broadcastAlertType: Yup.number().required(
      'Broadcast Alert Type is required'
    ),
  });

  const onClose = () => {
    navigate(PAGE_ROUTES.BROADCASTNOTIFICATION);
  };

  return (
    <Box sx={{ margin: 2 }}>
      <Formik
        initialValues={{
          title: '',
          roleId: [],
          broadcastAlertType: 1,
        }}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={async (values: any) => {
          const { title, broadcastAlertType, roleId } = values;
          const selectedRoles = roleId.map((roleKey: any) => teams[roleKey]);

          const roleIds = Array.from(
            new Set<number>(selectedRoles.map((r: any) => r.roleId))
          );
          const teamIds = Array.from(
            new Set<number>(
              selectedRoles.map((r: any) => r.teamId).filter(Boolean)
            )
          );

          const data = {
            message: title,
            messageDescription: editorContent,
            broadcastAlertType,
            roleIds,
            ...(teamIds.length > 0 && { teamIds }),
          };

          try {
            await HttpService.post(API_ROUTES.addBroadcastNotification, data);
            navigate(PAGE_ROUTES.BROADCASTNOTIFICATION);
          } catch (error) {
            console.error('Submission error:', error);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          isValid,
          setFieldValue,
        }) => (
          <Form>
            <Card sx={{ p: 3 }}>
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(1, 1fr)',
                }}
                sx={{ marginTop: '10px' }}
              >
                <Box>
                  {' '}
                  <Field
                    name="title"
                    as={TextField}
                    label="Title"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    helperText={
                      touched.title && errors.title ? errors.title : null
                    }
                    error={touched.title && !!errors.title}
                  />
                  <Box display="flex" gap={2}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="broadcast-alert-type-label">
                        Broadcast Alert Type
                      </InputLabel>
                      <Field
                        name="broadcastAlertType"
                        as={Select}
                        labelId="broadcast-alert-type-label"
                        label="Broadcast Alert Type"
                        helperText={
                          touched.broadcastAlertType &&
                          errors.broadcastAlertType
                            ? errors.broadcastAlertType
                            : null
                        }
                        error={
                          touched.broadcastAlertType &&
                          !!errors.broadcastAlertType
                        }
                      >
                        {Object.entries(BROADCAST_NOTIFICATION_TYPE).map(
                          ([key, value]) => (
                            <MenuItem key={value} value={value}>
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                            </MenuItem>
                          )
                        )}
                      </Field>
                      <ErrorMessage name="broadcastAlertType" component="div" />
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="role-select-label">
                        Select Role
                      </InputLabel>
                      <Field
                        name="roleId"
                        as={Select}
                        multiple
                        labelId="role-select-label"
                        label="Select Role"
                        onChange={(event: any) =>
                          setFieldValue('roleId', event.target.value)
                        }
                      >
                        {Object.keys(teams).map((key) => (
                          <MenuItem key={key} value={key}>
                            {key.replace(/_/g, ' ')}
                          </MenuItem>
                        ))}
                      </Field>
                      <ErrorMessage name="roleId" component="div" />
                    </FormControl>
                  </Box>
                </Box>

                <Editor
                  apiKey={process.env.REACT_APP_TINYMCEAPIKEY}
                  init={{
                    plugins: [
                      // Core editing features
                      'anchor',
                      'autolink',
                      'charmap',
                      'codesample',
                      'emoticons',
                      'image',
                      'link',
                      'lists',
                      'media',
                      'searchreplace',
                      'table',
                      'visualblocks',
                      'wordcount',
                    ],
                    toolbar:
                      'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table mergetags | addcomment showcomments | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
                  }}
                  initialValue="Notification Description Here!"
                  onEditorChange={(content) => setEditorContent(content)} // Update editor content
                />
              </Box>
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: 2,
                  gap: 2,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    !isValid ||
                    !touched.title ||
                    !touched.roleId
                  }
                >
                  Add Broadcast Notification
                </LoadingButton>
              </Stack>
            </Card>
          </Form>
        )}
      </Formik>

      {/* Backdrop with CircularProgress for loader */}
      <Backdrop
        sx={(theme) => ({
          color: '#fff',
          zIndex: theme.zIndex.drawer + 1,
        })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}

export default AddBroadcastNotification;
